import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import MpTopbar from "@/components/marketplace/MpTopbar";
import MpFooter from "@/components/marketplace/MpFooter";
import MpSearchBar from "@/components/marketplace/MpSearchBar";
import VenueCard from "@/components/marketplace/VenueCard";
import ReviewCard from "@/components/marketplace/ReviewCard";
import { ChevronLeft, ChevronRight, Sparkles, TrendingUp, Award } from "lucide-react";
import "@/styles/marketplace.css";

/**
 * Marketplace Home (Fresha-style discovery).
 * Sections: Hero · Recommended · New · Trending · Browse by Area · Reviews
 * The "X appointments booked today" counter is intentionally hidden in this
 * pilot phase (numbers small → bad social proof).
 */
export default function MarketplaceHome() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const [listings, setListings] = useState([]);
  const [areas, setAreas] = useState([]); // areas that host ≥1 salon
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, a, c] = await Promise.all([
          api.get("/marketplace/search?limit=24"),
          api.get("/marketplace/areas-with-venues"),
          api.get("/marketplace/categories"),
        ]);
        if (!alive) return;
        setListings(s.data?.items || []);
        setAreas(a.data?.items || []);
        setCategories(c.data?.items || []);
      } catch (e) {
        // swallow — render empty states gracefully
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Partitions: prefer featured for recommended, newest for new, top-rated for trending.
  const recommended = useMemo(() => {
    const featured = listings.filter(l => l.featured_until && new Date(l.featured_until) > new Date());
    return (featured.length ? featured : listings).slice(0, 8);
  }, [listings]);
  const newest = useMemo(() => [...listings].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 8), [listings]);
  const trending = useMemo(() => [...listings].sort((a, b) => (b.popularity_score || 0) - (a.popularity_score || 0)).slice(0, 8), [listings]);

  // Demo reviews fallback when DB has none yet (pilot phase).
  const demoReviews = isAr ? [
    { rating: 5, comment: { ar: "خدمة احترافية وأجواء راقية، عدت لزيارة الصالون مرات عديدة!" }, author_name: "نورة", city: "الدوحة" },
    { rating: 5, comment: { ar: "أحب طريقة الحجز السهلة عبر iSaloon." }, author_name: "سارة", city: "الريان" },
    { rating: 4, comment: { ar: "الموظفات لطيفات والمكان نظيف." }, author_name: "موزة", city: "لوسيل" },
    { rating: 5, comment: { ar: "تجربة سبا مذهلة، أنصح بها بشدة." }, author_name: "هند", city: "الوكرة" },
  ] : [
    { rating: 5, comment: { en: "Loved the experience — professional and elegant." }, author_name: "Noura", city: "Doha" },
    { rating: 5, comment: { en: "Booking through iSaloon is so easy." }, author_name: "Sara", city: "Al Rayyan" },
    { rating: 4, comment: { en: "Staff were lovely and the place spotless." }, author_name: "Moza", city: "Lusail" },
    { rating: 5, comment: { en: "Amazing spa experience — highly recommend." }, author_name: "Hind", city: "Al Wakra" },
  ];

  return (
    <div className="mp-root" data-testid="mp-home" dir={isAr ? "rtl" : "ltr"}>
      <MpTopbar variant="solid" />

      {/* ============== Hero ============== */}
      <section className="mp-hero-gradient">
        <div className="mp-container py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-center text-[var(--mp-black)]">
            {isAr ? (
              <>احجزي تجربة جمالك<br />في أرقى صالونات قطر</>
            ) : (
              <>Book beauty experiences<br />at Qatar&apos;s finest venues</>
            )}
          </h1>
          <p className="text-center text-base md:text-lg text-[var(--mp-ink-muted)] mt-5 max-w-2xl mx-auto">
            {isAr ? "اكتشفي المئات من الصالونات وسبا حول قطر، اقرأي التقييمات، واحجزي بنقرة واحدة." : "Discover hundreds of salons and spas across Qatar, read real reviews, and book in one tap."}
          </p>

          <div className="mt-8">
            <MpSearchBar variant="hero" />
          </div>
        </div>
      </section>

      {/* ============== Recommended ============== */}
      {recommended.length > 0 && (
        <Section
          icon={<Award size={18} />}
          eyebrow={isAr ? "موصى بها" : "Recommended"}
          title={isAr ? "صالونات نختارها لك" : "Venues picked for you"}
          subtitle={isAr ? "تشكيلة من أفضل الصالونات في قطر." : "A curated selection of the finest venues in Qatar."}
          testId="mp-recommended"
        >
          <CardGrid items={recommended} />
        </Section>
      )}

      {/* ============== Browse by Area ============== */}
      {areas.length > 0 && (
        <section className="mp-section bg-[var(--mp-cream)]" data-testid="mp-browse-area">
          <div className="mp-container">
            <SectionHeading
              eyebrow={isAr ? "تصفح حسب المنطقة" : "Browse by area"}
              title={isAr ? "اعثري على صالون قريب منك" : "Find a venue near you"}
              subtitle={isAr ? "كل منطقة تعرض الصالونات المسجّلة فيها فقط." : "Each area lists only the salons currently registered there."}
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
              {areas.slice(0, 12).map((a) => {
                const areaName = isAr ? (a.name?.ar || a.name?.en) : (a.name?.en || a.name?.ar);
                const muniName = isAr ? (a.municipality_name?.ar || a.municipality_name?.en) : (a.municipality_name?.en || a.municipality_name?.ar);
                return (
                  <Link
                    key={a.area_id}
                    to={`/m/search?area=${a.area_id}`}
                    data-testid={`mp-area-${a.area_id}`}
                    className="mp-card-flat p-5 hover:shadow-md hover:-translate-y-1 transition group cursor-pointer flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-lg font-bold text-[var(--mp-black)] mb-0.5 truncate">{areaName}</div>
                      {muniName && <div className="text-xs text-[var(--mp-ink-muted)] truncate">{muniName}</div>}
                    </div>
                    <div className="shrink-0 text-end">
                      <div className="text-xl font-bold text-[var(--mp-maroon-dark)] num-display">{a.venue_count}</div>
                      <div className="text-[10px] text-[var(--mp-ink-muted)] uppercase tracking-wider">{isAr ? "صالون" : a.venue_count === 1 ? "salon" : "salons"}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============== New + Trending ============== */}
      {newest.length > 0 && (
        <Section
          icon={<Sparkles size={18} />}
          eyebrow={isAr ? "جديد على iSaloon" : "New on iSaloon"}
          title={isAr ? "صالونات أضيفت حديثاً" : "Fresh on the platform"}
          subtitle={isAr ? "آخر الصالونات التي انضمت إلى iSaloon." : "The latest venues to join iSaloon."}
          testId="mp-new"
        >
          <CardGrid items={newest} />
        </Section>
      )}

      {trending.length > 0 && (
        <Section
          icon={<TrendingUp size={18} />}
          eyebrow={isAr ? "الأكثر طلباً" : "Trending"}
          title={isAr ? "ما يحبه الناس الآن" : "What people love right now"}
          subtitle={isAr ? "الصالونات الأكثر حجزاً هذا الأسبوع." : "The most-booked venues this week."}
          testId="mp-trending"
        >
          <CardGrid items={trending} />
        </Section>
      )}

      {/* ============== Reviews ============== */}
      <section className="mp-section bg-[var(--mp-cream)]" data-testid="mp-reviews">
        <div className="mp-container">
          <SectionHeading
            eyebrow={isAr ? "تقييمات حقيقية" : "Real reviews"}
            title={isAr ? "ما يقوله العملاء" : "What customers say"}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {demoReviews.map((r, i) => <ReviewCard key={i} review={r} />)}
          </div>
        </div>
      </section>

      {/* ============== CTA strip ============== */}
      <section className="mp-section">
        <div className="mp-container">
          <div className="rounded-2xl px-8 md:px-12 py-12 md:py-16 text-center mp-hero-gradient relative overflow-hidden">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--mp-black)]">
              {isAr ? "صاحب صالون؟ اعرض خدماتك هنا." : "Run a salon? List your business with us."}
            </h2>
            <p className="text-[var(--mp-ink-muted)] mt-3 max-w-2xl mx-auto">
              {isAr ? "اربط نظام إدارتك بـ iSaloon Marketplace، واستقبل الحجوزات تلقائياً." : "Connect your management system to iSaloon Marketplace and accept bookings automatically."}
            </p>
            <Link to="/signup" className="mp-btn mp-btn-primary mt-6 !px-7 !py-3 inline-flex">
              {isAr ? "ابدأ التسجيل" : "Get started"}
            </Link>
          </div>
        </div>
      </section>

      <MpFooter />
    </div>
  );
}

// =================== Helpers ===================

function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center max-w-3xl mx-auto">
      {eyebrow && <div className="inline-block bg-[var(--mp-maroon-tint)] text-[var(--mp-maroon-dark)] text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">{eyebrow}</div>}
      <h2 className="text-3xl md:text-4xl font-bold text-[var(--mp-black)]">{title}</h2>
      {subtitle && <p className="text-[var(--mp-ink-muted)] mt-3 text-base">{subtitle}</p>}
    </div>
  );
}

function Section({ icon, eyebrow, title, subtitle, children, testId }) {
  return (
    <section className="mp-section" data-testid={testId}>
      <div className="mp-container">
        <SectionHeading eyebrow={eyebrow} title={title} subtitle={subtitle} />
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

function CardGrid({ items }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {items.map((l) => <VenueCard key={l.listing_id} l={l} />)}
    </div>
  );
}
